/**
 * O diálogo de encerrar, reabrir ou excluir uma organização (#767).
 *
 * Diz o que a ação faz antes de ela acontecer, pede o motivo quando a ação
 * pede, e depois mostra o que não terminou. Excluir passa ainda pelo segundo
 * fator, que quem monta a tela abre por cima deste diálogo, com o motivo já
 * escrito aqui.
 *
 * A tela só entrega o pedido: quem chama a API, invalida o que mudou e avisa é
 * a página.
 */

import { useEffect, useState, type ReactNode } from 'react';
import { Botao, CampoArea, Dialogo } from '@/shared/ui';
import { Faixa } from '@/shared/ui/estados/faixa';
import {
  MAIOR_JUSTIFICATIVA,
  motivoDaJustificativa,
  pedeJustificativa,
  type AcaoDeSituacao,
  type ResultadoDaAcao,
} from '../lib/situacao';
import './situacao.css';

export type DialogoDeSituacaoProps = {
  acao: AcaoDeSituacao;
  organizacao: { readonly nome: string; readonly identificador: string };
  enviando: boolean;
  /** A recusa do servidor, para dizer no próprio diálogo. */
  falha: string | null;
  /** Depois de feito, o que não terminou e o que fica para depois; nulo antes. */
  resultado: ResultadoDaAcao | null;
  /**
   * A justificativa que o segundo fator concedeu à exclusão: toma o campo, para
   * a pessoa ver e repetir a que vale, e não a escrita antes dele (#1160).
   */
  justificativaConcedida?: string | null;
  aoConfirmar: (justificativa: string | null) => void;
  aoFechar: () => void;
};

const TEXTOS: Readonly<
  Record<
    AcaoDeSituacao,
    {
      readonly titulo: string;
      readonly botao: string;
      readonly enviando: string;
      readonly feito: string;
      readonly pergunta: string;
    }
  >
> = {
  encerrar: {
    titulo: 'Encerrar a organização',
    botao: 'Encerrar organização',
    enviando: 'Encerrando…',
    feito: 'A organização foi encerrada.',
    pergunta: 'Por que encerrar?',
  },
  reabrir: {
    titulo: 'Reabrir a organização',
    botao: 'Reabrir organização',
    enviando: 'Reabrindo…',
    feito: 'A organização foi reaberta.',
    pergunta: '',
  },
  excluir: {
    titulo: 'Excluir a organização de vez',
    botao: 'Excluir de vez',
    enviando: 'Excluindo…',
    feito: 'A organização foi excluída.',
    pergunta: 'Por que excluir?',
  },
};

function OQueAcontece({
  acao,
  nome,
  identificador,
}: {
  acao: AcaoDeSituacao;
  nome: string;
  identificador: string;
}): ReactNode {
  const quem = (
    <>
      <strong>{nome}</strong> ({identificador})
    </>
  );
  switch (acao) {
    case 'encerrar':
      return (
        <p id="situacao-texto" className="dialogo__texto">
          {quem} deixa de funcionar: ninguém mais entra, e as conexões com os canais são desligadas.
          Nada é apagado, e reabrir desfaz.
        </p>
      );
    case 'reabrir':
      return (
        <p id="situacao-texto" className="dialogo__texto">
          {quem} volta a funcionar: as pessoas voltam a entrar, e as conexões com os canais são
          religadas.
        </p>
      );
    case 'excluir':
      return (
        <>
          <p id="situacao-texto" className="dialogo__texto">
            Apaga o acesso e a conta de atendimento de {quem}, com as conversas, os contatos e as
            caixas. Isso não volta.
          </p>
          <p className="dialogo__apoio">
            Tudo o que o CliqCRM guarda dela (conversas, clientes, comercial, arquivos e eventos)
            fica até a purga do prazo de retenção, que por enquanto é feita à mão.
          </p>
          <p className="dialogo__apoio">
            Depois de escrever o motivo, você confirma a sua identidade com o segundo fator.
          </p>
        </>
      );
  }
}

export function DialogoDeSituacao({
  acao,
  organizacao,
  enviando,
  falha,
  resultado,
  justificativaConcedida = null,
  aoConfirmar,
  aoFechar,
}: DialogoDeSituacaoProps): ReactNode {
  const [justificativa, definirJustificativa] = useState('');
  useEffect(() => {
    if (justificativaConcedida) definirJustificativa(justificativaConcedida);
  }, [justificativaConcedida]);
  const [tentou, definirTentou] = useState(false);
  const textos = TEXTOS[acao];
  const comMotivo = pedeJustificativa(acao);
  const falta = comMotivo ? motivoDaJustificativa(justificativa) : null;

  if (resultado) {
    return (
      <Dialogo
        titulo={textos.titulo}
        aoFechar={aoFechar}
        largura="estreita"
        botoes={
          <Botao variante="primario" onClick={aoFechar}>
            Fechar
          </Botao>
        }
      >
        <p className="dialogo__texto">{textos.feito}</p>
        {resultado.partesQueFalharam.length > 0 ? (
          <Faixa tom="atencao">
            Não terminou em: {resultado.partesQueFalharam.join(', ')}. Confira com o time técnico
            antes de repetir.
          </Faixa>
        ) : null}
        {resultado.pendencias.length > 0 ? (
          <>
            <p className="dialogo__acao">O que fica para fazer depois</p>
            <ul className="situacao__pendencias" aria-label="O que fica para fazer depois">
              {resultado.pendencias.map((pendencia) => (
                <li key={pendencia}>{pendencia}</li>
              ))}
            </ul>
          </>
        ) : null}
      </Dialogo>
    );
  }

  const confirmar = () => {
    definirTentou(true);
    if (falta !== null) return;
    aoConfirmar(comMotivo ? justificativa.trim() : null);
  };

  // Enquanto a ação roda, Esc, o X e o véu não fecham (#1160): a resposta que
  // chegasse depois fecharia ou trocaria o diálogo aberto naquela hora, e a
  // tela ofereceria pedir a mesma exclusão de novo.
  const fechar = () => {
    if (!enviando) aoFechar();
  };

  return (
    <Dialogo
      titulo={textos.titulo}
      aoFechar={fechar}
      largura="estreita"
      descricaoId="situacao-texto"
      consequencia={acao === 'excluir'}
      botoes={
        <>
          <Botao
            variante="fantasma"
            desabilitado={enviando}
            motivo={textos.enviando}
            onClick={aoFechar}
          >
            Cancelar
          </Botao>
          <Botao
            type="submit"
            form="situacao-formulario"
            variante={acao === 'reabrir' ? 'primario' : 'perigo'}
            desabilitado={enviando}
            motivo={textos.enviando}
          >
            {enviando ? textos.enviando : textos.botao}
          </Botao>
        </>
      }
    >
      <form
        id="situacao-formulario"
        className="situacao__formulario"
        onSubmit={(evento) => {
          evento.preventDefault();
          confirmar();
        }}
      >
        <OQueAcontece
          acao={acao}
          nome={organizacao.nome}
          identificador={organizacao.identificador}
        />
        {comMotivo ? (
          <CampoArea
            rotulo={textos.pergunta}
            ajuda="Fica registrado com o seu nome no histórico da organização."
            rows={3}
            maxLength={MAIOR_JUSTIFICATIVA}
            value={justificativa}
            erro={tentou ? (falta ?? undefined) : undefined}
            onChange={(evento) => definirJustificativa(evento.target.value)}
          />
        ) : null}
        {falha ? (
          <p className="dialogo__erro" role="alert">
            {falha}
          </p>
        ) : null}
      </form>
    </Dialogo>
  );
}
